import React, { useState, useEffect } from 'react';
import { updateOrderStatus, deleteOrder } from '../services/api';
import EditOrderModal from './EditOrderModal';
import OrderHistory from './OrderHistory';
import './OrdersList.css';

function OrdersList({ orders, user, onOrderUpdated, onOrderDeleted }) {
  const [filter, setFilter] = useState('all');
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [historyOrderId, setHistoryOrderId] = useState(null);
  // Details are shown by default now; persist view in localStorage
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem('ordersView') || 'grid';
    } catch (e) {
      return 'grid';
    }
  }); // 'grid' or 'list'

  useEffect(() => {
    try {
      localStorage.setItem('ordersView', view);
    } catch (e) {
      // ignore
    }
  }, [view]);

  const statusLabels = {
    pending: 'En attente',
    inprogress: 'En cours',
    ready: 'Prêt',
    completed: 'Terminé'
  };

  const statusColors = {
    pending: '#ff9800',
    inprogress: '#2196f3',
    ready: '#4caf50',
    completed: '#9e9e9e'
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const result = await updateOrderStatus(orderId, newStatus);
    if (result.success) {
      onOrderUpdated();
    } else {
      alert(result.message || 'Erreur lors de la mise à jour du statut');
    }
  };

  const handleDelete = (orderId) => {
    setDeleteConfirmation(orderId);
  };

  const confirmDelete = async (orderId) => {
    const result = await deleteOrder(orderId);
    setDeleteConfirmation(null);
    if (result.success) {
      onOrderDeleted();
    } else {
      alert(result.message || 'Erreur lors de la suppression');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'ready') return order.status === 'ready' || order.status === 'completed';
    return order.status === filter;
  });

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const dateA = a.pickupDate ? new Date(a.pickupDate).getTime() : 0;
    const dateB = b.pickupDate ? new Date(b.pickupDate).getTime() : 0;
    return dateA - dateB;
  });

  return (
    <div className="orders-list">
      <div className="orders-header">
        <h2>Liste des commandes ({filteredOrders.length})</h2>
        <div style={{display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between', flex: 1}}>
          <div className="filter-buttons">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            Toutes ({orders.length})
          </button>
          <button
            className={filter === 'pending' ? 'active' : ''}
            onClick={() => setFilter('pending')}
          >
            En attente ({orders.filter(o => o.status === 'pending').length})
          </button>
          <button
            className={filter === 'inprogress' ? 'active' : ''}
            onClick={() => setFilter('inprogress')}
          >
            En cours ({orders.filter(o => o.status === 'inprogress').length})
          </button>
          <button
            className={filter === 'ready' ? 'active' : ''}
            onClick={() => setFilter('ready')}
          >
            Terminé ({orders.filter(o => o.status === 'ready' || o.status === 'completed').length})
          </button>
          </div>

          <div className="view-toggle">
            <button
              className={view === 'grid' ? 'active' : ''}
              onClick={() => setView('grid')}
              title="Vue grille"
            >
              ⊞
            </button>
            <button
              className={view === 'list' ? 'active' : ''}
              onClick={() => setView('list')}
              title="Vue liste"
            >
              ≡
            </button>
          </div>
        </div>
      </div>

      {sortedOrders.length === 0 ? (
        <div className="no-orders">
          <p>Aucune commande à afficher</p>
        </div>
      ) : (
        <div className={view === 'grid' ? 'orders-grid' : 'orders-list-view'}>
          {sortedOrders.map(order => {
            const itemsArray = Array.isArray(order.items) ? order.items : (order.items ? Object.values(order.items) : []);
            return (
            <div key={order.id} className="order-card">
              <div className="order-card-header">
                <div>
                  <h3 className="order-title">
                    {new Date(order.pickupDate).toLocaleDateString('fr-FR')} • {order.pickupTime} • {order.pickupLocation}
                  </h3>
                  <p className="order-customer">{order.customerName}</p>
                </div>
                <span
                  className="status-badge"
                  style={{ backgroundColor: statusColors[order.status] }}
                >
                  {statusLabels[order.status]}
                </span>
              </div>

              <div className="order-info">
                {!view === 'list' && (
                  <>
                    <div className="info-row">
                      <span className="info-label">Retrait:</span>
                      <span>{order.pickupLocation}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Date:</span>
                      <span>{new Date(order.pickupDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Heure:</span>
                      <span>{order.pickupTime}</span>
                    </div>
                  </>
                )}
                {/* Prix masqué par défaut */}
              </div>

              <div className="order-details">
                  <h4>Articles commandés:</h4>
                  <ul className="items-list">
                    {itemsArray.map((item, index) => {
                      const qty = item.quantity || 1;
                      return (
                        <li key={index}>
                          <span>{item.name} - {qty} personne{qty > 1 ? 's' : ''}</span>
                        </li>
                      );
                    })}
                  </ul>

                  {order.orderComment && (
                    <div className="order-comment">
                      <strong>Commentaire:</strong>
                      <p>{order.orderComment}</p>
                    </div>
                  )}
              </div>

              <div className="order-actions">
                {user.role === 'vendeur' && order.status === 'pending' && (
                  <button
                    onClick={() => setEditingOrder(order)}
                    className="edit-button"
                  >
                    ✎ Modifier
                  </button>
                )}
                <button
                  onClick={() => setHistoryOrderId(order.id)}
                  className="history-button"
                >
                  ⋮ Historique
                </button>
                {user.role === 'patissier' && (
                  <>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">En attente</option>
                      <option value="inprogress">En cours</option>
                      <option value="completed">Terminé</option>
                    </select>
                    <button
                      onClick={() => handleDelete(order.id)}
                      className="delete-button"
                    >
                      Supprimer
                    </button>
                  </>
                )}
              </div>

            </div>
          ); })}
        </div>
      )}

      {deleteConfirmation && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-content">
            <h2>Supprimer la commande ?</h2>
            <p>Cette action est irréversible. Êtes-vous certain de vouloir supprimer cette commande ?</p>
            <div className="delete-modal-actions">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="delete-modal-cancel"
              >
                Annuler
              </button>
              <button
                onClick={() => confirmDelete(deleteConfirmation)}
                className="delete-modal-confirm"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {editingOrder && (
        <EditOrderModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onOrderUpdated={onOrderUpdated}
        />
      )}

      <OrderHistory
        orderId={historyOrderId}
        isOpen={!!historyOrderId}
        onClose={() => setHistoryOrderId(null)}
      />
    </div>
  );
}

export default OrdersList;
